/**
 * SummaryEditor — compact TipTap editor for the AI treatments summary text,
 * living in the sidebar of AiSummaryPage. Same document typography as the
 * forms editor (.ck-form-doc via styles/formDocument.css) so the text matches
 * the paper preview and the PDF, without form-only blocks.
 *
 * "מצב מיקוד" (focus mode) is a CSS state on the same mounted instance — the
 * editor never remounts, so content/undo history survive toggling. The editor
 * re-seeds its content only when `resetKey` changes (AI result / report seed).
 */

import { useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import TextAlign from "@tiptap/extension-text-align";
import {
  TextB,
  TextItalic,
  TextHOne,
  TextHTwo,
  ListBullets,
  ListNumbers,
  ArrowUUpLeft,
  ArrowUUpRight,
  ArrowsOutSimple,
  ArrowsInSimple,
} from "@phosphor-icons/react";
import "../../../styles/formDocument.css";

const extensions = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    code: false,
    codeBlock: false,
    link: false,
    blockquote: false,
    horizontalRule: false,
    dropcursor: { width: 2 },
  }),
  Placeholder.configure({
    placeholder: "הסיכום שייווצר יופיע כאן לעריכה — או כתבו כאן טקסט חופשי…",
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
    alignments: ["right", "center", "left", "justify"],
    defaultAlignment: null,
  }),
];

function ToolbarButton({ onClick, active, disabled, label, children }) {
  return (
    <button
      type="button"
      className={`ais-tb-btn ${active ? "is-active" : ""}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function SummaryEditor({ value, resetKey, onChange, fullscreen, onToggleFullscreen }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debounceRef = useRef(null);

  const initialContent = useMemo(() => value || "", [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const editor = useEditor(
    {
      extensions,
      content: initialContent,
      editorProps: {
        attributes: {
          class: "ck-form-doc ais-paper-content",
          dir: "rtl",
          lang: "he",
          spellcheck: "true",
        },
      },
      onUpdate: ({ editor: ed }) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onChangeRef.current?.(ed.getHTML());
        }, 300);
      },
    },
    [resetKey]
  );

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Esc leaves focus mode.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onToggleFullscreen?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen, onToggleFullscreen]);

  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) return null;
      return {
        bold: ed.isActive("bold"),
        italic: ed.isActive("italic"),
        h2: ed.isActive("heading", { level: 2 }),
        h3: ed.isActive("heading", { level: 3 }),
        bullet: ed.isActive("bulletList"),
        ordered: ed.isActive("orderedList"),
        canUndo: ed.can().undo(),
        canRedo: ed.can().redo(),
      };
    },
  });
  const run = (fn) => () => {
    if (!editor) return;
    fn(editor.chain().focus()).run();
  };

  return (
    <div className={`ais-editor-shell ${fullscreen ? "fullscreen" : ""}`}>
      <div className="ais-toolbar" role="toolbar" aria-label="עיצוב טקסט">
        <div className="ais-tb-group">
          <ToolbarButton label="מודגש" active={state?.bold} onClick={run((c) => c.toggleBold())}>
            <TextB size={16} weight="bold" />
          </ToolbarButton>
          <ToolbarButton label="נטוי" active={state?.italic} onClick={run((c) => c.toggleItalic())}>
            <TextItalic size={16} />
          </ToolbarButton>
        </div>
        <div className="ais-tb-group">
          <ToolbarButton
            label="כותרת"
            active={state?.h2}
            onClick={run((c) => c.toggleHeading({ level: 2 }))}
          >
            <TextHOne size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="כותרת משנה"
            active={state?.h3}
            onClick={run((c) => c.toggleHeading({ level: 3 }))}
          >
            <TextHTwo size={16} />
          </ToolbarButton>
        </div>
        <div className="ais-tb-group">
          <ToolbarButton label="רשימה" active={state?.bullet} onClick={run((c) => c.toggleBulletList())}>
            <ListBullets size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="רשימה ממוספרת"
            active={state?.ordered}
            onClick={run((c) => c.toggleOrderedList())}
          >
            <ListNumbers size={16} />
          </ToolbarButton>
        </div>
        <div className="ais-tb-group">
          <ToolbarButton label="בטל" disabled={!state?.canUndo} onClick={run((c) => c.undo())}>
            <ArrowUUpRight size={16} />
          </ToolbarButton>
          <ToolbarButton label="בצע שוב" disabled={!state?.canRedo} onClick={run((c) => c.redo())}>
            <ArrowUUpLeft size={16} />
          </ToolbarButton>
        </div>
        <div className="ais-tb-spacer" />
        <button
          type="button"
          className="ais-tb-btn ais-tb-focus"
          onClick={onToggleFullscreen}
          title={fullscreen ? "יציאה ממצב מיקוד" : "מצב מיקוד"}
          aria-label={fullscreen ? "יציאה ממצב מיקוד" : "מצב מיקוד"}
        >
          {fullscreen ? <ArrowsInSimple size={16} /> : <ArrowsOutSimple size={16} />}
          <span className="ais-tb-label">{fullscreen ? "סגירה" : "מצב מיקוד"}</span>
        </button>
      </div>

      <div className="ais-canvas">
        <div className="ais-paper">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}

export default SummaryEditor;
